import type {Subtitle} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle';
import {BACKGROUND_PADDING, createSubtitleFromObjectAndAddToTranscript} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle';
import type {CopyableItemStyles} from '@PosterWhiteboard/items/item/item.types';
import {type InitItemOpts, ITEM_TYPE, type ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {Item} from '@PosterWhiteboard/items/item/item.class';
import type {SubtitleObject, SubtitleStylesAndAnimation} from '@PosterWhiteboard/items/transcript-item/subtitle/subtitle.types';
import type {TranscriptGeneratedFrom, TranscriptItemObject, TranscriptUpdateFromObjectOpts} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import {TRANSCRIPT_ITEM_PADDING} from '@PosterWhiteboard/items/transcript-item/transcript-item.types';
import type {FabricObject, ObjectEvents, SerializedShadowOptions, Shadow} from '@postermywall/fabricjs-2';
import {Canvas, FixedLayout, Group, IText, LayoutManager, Point, Rect, Textbox} from '@postermywall/fabricjs-2';
import {TEXT_OUTLINE_STROKE_WIDTH_FACTOR, TextVerticalAlignType} from '@PosterWhiteboard/classes/text-styles.class';
import type {LetterCase} from '@Utils/string.util';
import {applyLetterCase} from '@Utils/string.util';
import type {Page} from '@PosterWhiteboard/page/page.class';
import {addItemsToGroupWithOriginalScale} from '@Utils/fabric.util';
import {SubtitleTemplateType} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles.types';
import {getFontFamilyNameForVariations, getRecommendedFont, getScriptForText, isRTLScript} from '@Libraries/font-library';
import {convertFabricColorToFillObject, rgbaToHexString} from '@Utils/color.util';
import type {DeepPartial} from '@/global';
import {noop} from '@Utils/general.util';
import {hideCustomControls, unhideCustomControls} from '@PosterWhiteboard/libraries/custom-item-controls.library';
import {BorderType} from '@PosterWhiteboard/classes/item-border.class';
import {createItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import {convertFabricShadowObjectToShadowOrGlowOptionsObject, convertShadowOptionsObjectToFabricShadowObject, type ShadowOrGlowOptionsObject} from '@Utils/fabric-shadow.util';
import {getAllAvailableTemplateStylesAndProperties, getDefaultSelectedSubtitleTemplateProperties} from '@PosterWhiteboard/items/transcript-item/subtitle/template-styles';
import type {NewTranscriptData} from '@PosterWhiteboard/page/add-item.class';
import {DEFAULT_FONT_SIZE} from '@PosterWhiteboard/page/add-item.class';
import {ceilPrecision} from '@Utils/math.util';

const NEW_TRANSCRIPT_ITEM_OFFSET_FROM_BOTTOM = 150;

export class TranscriptItem extends Item {
  declare _fabricObject: Group;
  public gitype = ITEM_TYPE.TRANSCRIPT;

  public subtitlesHashmap: Record<string, Subtitle> = {};
  // Ephemeral selection state — the subtitle currently selected on the timeline.
  // Lives on the model (like the audio selection on AudioClips); never persisted.
  public activeSubtitleId = '';
  public verticalAlign: TextVerticalAlignType = TextVerticalAlignType.BOTTOM;
  public generatedFrom?: TranscriptGeneratedFrom;

  public previewImageUrl: string | undefined;

  public areFontsLoaded = false;
  public smallestWidthNeeded: number | undefined;

  private wasItemSelectedOnMouseDown = false;

  private readonly boundBringToFront;

  public constructor(page: Page) {
    super(page);
    this.boundBringToFront = this.bringToFront.bind(this);
  }

  public override isItemForTimelineItemTrack(): boolean {
    return false;
  }

  override get fabricObject(): Group {
    return this._fabricObject;
  }

  public override toObject(): TranscriptItemObject {
    const subtitleObjects: Record<string, SubtitleObject> = {};

    for (const [key, item] of Object.entries(this.subtitlesHashmap)) {
      subtitleObjects[key] = item.toObject();
    }

    return {
      ...super.toObject(),
      shadow: null,
      subtitlesHashmap: subtitleObjects,
      generatedFrom: this.generatedFrom,
      verticalAlign: this.verticalAlign,
      activeSubtitleId: this.activeSubtitleId,
    };
  }

  public setPreviewImageUrl(): void {
    const textbox = new Textbox('Aa', {
      lockMovementY: true,
      lockMovementX: true,
      lockScalingX: true,
      lockScalingY: true,
      hasControls: false,
      left: BACKGROUND_PADDING,
      top: BACKGROUND_PADDING,
    });

    const background = new Rect({
      width: textbox.width + BACKGROUND_PADDING * 2,
      height: textbox.height + BACKGROUND_PADDING * 2,
      strokeWidth: 0,
      evented: false,
      selectable: false,
    });

    const textStylesToUse = this.getAnySubtitle().sentenceTextStyles;

    textbox.set({
      ...textStylesToUse.getTextStyles(this.getAnySubtitle().backgroundFabricObject.width, this.getAnySubtitle().backgroundFabricObject.height),
      charSpacing: 10,
      shadow: this.getAnySubtitle().getShadow(),
      fontFamily: textStylesToUse.fontFamily,
    });

    if (textStylesToUse.stroke) {
      textbox.set({
        strokeWidth: textStylesToUse.fontSize * textStylesToUse.strokeWidth * TEXT_OUTLINE_STROKE_WIDTH_FACTOR,
        strokeLineJoin: 'round',
        paintFirst: 'stroke',
        stroke: rgbaToHexString(textStylesToUse.strokeColor),
      });
    } else {
      textbox.set({
        strokeWidth: 0,
        strokeLineJoin: 'miter',
        paintFirst: 'fill',
        stroke: undefined,
      });
    }

    background.set({
      rx: this.getAnySubtitle().backgroundBorderRadius,
      ry: this.getAnySubtitle().backgroundBorderRadius,
      fill: this.getAnySubtitle().backgroundFill.getFill(background.width, background.height),
    });

    background.set({
      width: textbox.width + BACKGROUND_PADDING * 2,
      height: textbox.height + BACKGROUND_PADDING * 2,
    });

    const dummyFabricGroup = new Group([background, textbox], {
      subTargetCheck: true,
      interactive: true,
      layoutManager: new LayoutManager(new FixedLayout()),
      lockMovementY: true,
      lockMovementX: true,
      hasControls: false,
      selectable: false,
    });

    dummyFabricGroup.set({
      width: background.width,
      height: background.height,
    });

    background.setPositionByOrigin(new Point(0, 0), 'center', 'center');
    textbox.setPositionByOrigin(new Point(0, 0), 'center', 'center');

    this.previewImageUrl = dummyFabricGroup.toDataURL({format: 'jpeg', quality: 0.2});
  }

  public getPreviewImageUrl(): string | undefined {
    return this.previewImageUrl;
  }

  public override getFonts(withVariation: boolean): string[] {
    return [
      withVariation
        ? getFontFamilyNameForVariations(
            this.getAnySubtitle().sentenceTextStyles.fontFamily,
            this.getAnySubtitle().sentenceTextStyles.isBold,
            this.getAnySubtitle().sentenceTextStyles.isItalic
          )
        : this.getAnySubtitle().sentenceTextStyles.fontFamily,
    ];
  }

  public getSubtitleIdsInOrder(): string[] {
    return Object.entries(this.subtitlesHashmap)
      .sort(([, subtitleA], [, subtitleB]) => {
        const startTimeDifference = subtitleA.startTime - subtitleB.startTime;

        if (startTimeDifference !== 0) {
          return startTimeDifference;
        }

        return subtitleA.endTime - subtitleB.endTime;
      })
      .map(([uid]) => {
        return uid;
      });
  }

  public override getCopyableStyles(): CopyableItemStyles {
    return {
      ...super.getCopyableStyles(),
      verticalAlign: this.verticalAlign,
      ...this.getAnySubtitle().getCopyableStyles(),
    };
  }

  public async pasteStylesOnAllSubtitles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<SubtitleObject> = {};
    if (copiedStyles.activeWordTextStyles !== undefined) {
      stylesObj.activeWordTextStyles = copiedStyles.activeWordTextStyles;
    }
    if (copiedStyles.animationStyle !== undefined) {
      stylesObj.animationStyle = copiedStyles.animationStyle;
    }
    if (copiedStyles.textStyles !== undefined) {
      stylesObj.sentenceTextStyles = {
        ...copiedStyles.textStyles,
        isBold: false,
        isItalic: false,
        underLine: false,
        lineThrough: false,
      };
    }

    if (copiedStyles.frontFill !== undefined) {
      stylesObj.sentenceTextStyles = {
        ...stylesObj.sentenceTextStyles,
        fill: convertFabricColorToFillObject(copiedStyles.frontFill),
      };
    }

    if (copiedStyles.backFill !== undefined) {
      stylesObj.backgroundFill = copiedStyles.backFill;
    }
    if (copiedStyles.rx !== undefined) {
      stylesObj.backgroundBorderRadius = copiedStyles.rx;
    }
    if (copiedStyles.textBorder !== undefined) {
      stylesObj.sentenceTextStyles = {
        ...stylesObj.sentenceTextStyles,
        stroke: copiedStyles.textBorder.solidBorderType !== BorderType.NONE,
        strokeWidth: copiedStyles.textBorder.solidBorderThickness / 100,
        strokeColor: copiedStyles.textBorder.solidBorderColor,
      };
    }
    if (copiedStyles.shadow !== undefined) {
      stylesObj.shadow = copiedStyles.shadow;
    }

    await this.updateAllSubtitlesStylesFromSubtitleObject(stylesObj, {undoable: false});
  }

  public override async pasteStyles(copiedStyles: CopyableItemStyles): Promise<void> {
    const stylesObj: DeepPartial<TranscriptItemObject> = {};
    if (copiedStyles.verticalAlign !== undefined) {
      stylesObj.verticalAlign = copiedStyles.verticalAlign;
    }

    await this.pasteStylesOnAllSubtitles(copiedStyles);
    const commonStylesObj: DeepPartial<ItemObject> = this.createCommonStylesObject(copiedStyles, ['shadow', 'border']);
    await this.applyPastedStyles({...commonStylesObj, ...stylesObj});
  }

  public override async onItemAddedToPage(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        this.syncAllSubtitles();
        this.page.fabricCanvas.on('object:added', this.boundBringToFront);
        resolve();
      } catch (error) {
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  public override onRemove(): void {
    super.onRemove();
    this.page.fabricCanvas.off('object:added', this.boundBringToFront);
    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      subtitle.onRemove();
    }
  }

  public override canMoveInZIndex(): boolean {
    return false;
  }

  public override async updateFromObject(
    transcriptItemObject: DeepPartial<TranscriptItemObject>,
    {undoable = true, fireModifiedEvent = true, updateRedux = true, onError = noop}: TranscriptUpdateFromObjectOpts = {}
  ): Promise<void> {
    const {subtitlesHashmap, ...obj} = transcriptItemObject;

    this.copyVals({
      ...obj,
    });

    await this.updateFabricObjectFromObject(obj);

    if (subtitlesHashmap) {
      // delete items that are in this page but not in the transcriptItemObject
      for (const [uid] of Object.entries(this.subtitlesHashmap)) {
        if (subtitlesHashmap[uid] === undefined) {
          this.removeSubtitle(uid);
        }
      }

      const promises = [];

      for (const [uid, subtitleObject] of Object.entries(subtitlesHashmap)) {
        if (subtitleObject) {
          if (uid in this.subtitlesHashmap) {
            promises.push(
              this.subtitlesHashmap[uid].updateFromObject(subtitleObject, {
                undoable: false,
                updateRedux: false,
                onError: onError,
              })
            );
          } else {
            promises.push(createSubtitleFromObjectAndAddToTranscript(this, subtitleObject));
          }
        }
      }

      await Promise.all(promises);
    }

    if (!this.previewImageUrl) {
      this.setPreviewImageUrl();
    }

    this.ensureMinDimensionsAreSatisfied();

    await this.invalidate();

    if (undoable) {
      this.page.poster.history.addPosterHistory();
    }
    if (fireModifiedEvent) {
      this.page.fire('modified');
    }
    if (updateRedux) {
      this.page.poster.redux.updateReduxData();
    }

    this.syncAllSubtitles();

    this.page.fabricCanvas.requestRenderAll();
  }

  public override copyVals(obj: DeepPartial<TranscriptItemObject>): void {
    // Subtitles value is ignored n copy vals as they requrie async and only work with updatefromobject.
    // activeSubtitleId is ephemeral selection state, so undo/load must not restore it.
    const {subtitlesHashmap: _, activeSubtitleId: __, ...plainObj} = obj;
    super.copyVals(plainObj);
  }

  public getInitialCoordinatesForTranscript(count: number): {left: number; top: number} {
    const maxHeight = this.getLargestSubtitleFabricObjectHeight();

    const adjustedY = this.page.poster.height - maxHeight - TRANSCRIPT_ITEM_PADDING - (count - 1) * NEW_TRANSCRIPT_ITEM_OFFSET_FROM_BOTTOM;
    const yForCurrentlyAddedTranscriptItem = adjustedY >= TRANSCRIPT_ITEM_PADDING ? adjustedY : TRANSCRIPT_ITEM_PADDING;

    return {
      left: TRANSCRIPT_ITEM_PADDING,
      top: yForCurrentlyAddedTranscriptItem,
    };
  }

  public async updateFabricObjectPositionAndSize(count: number): Promise<void> {
    await this.updateFromObject(this.getInitialCoordinatesForTranscript(count), {updateRedux: false, undoable: false});
  }

  public override async updateFabricObject(): Promise<void> {
    await super.updateFabricObject();
    this.updateTranscriptGroupItemsDimensionsAndAlignment();
    this.fabricObject.setCoords();
  }

  public getAnySubtitle(): Subtitle {
    return Object.values(this.subtitlesHashmap)[0];
  }

  public seekToSubtitleAtTime(time: number): void {
    let subtitleAtTime: Subtitle | undefined;

    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      if (time >= subtitle.startTime && time <= subtitle.endTime) {
        subtitleAtTime = subtitle;
      } else {
        subtitle.hide();
      }
    }

    if (subtitleAtTime) {
      subtitleAtTime.show();
      subtitleAtTime.handleCurrentWordAtTime(time);
    }
  }

  public getSubtitleAtPageTime(pageTime: number): Subtitle | undefined {
    if (pageTime < 0) {
      return undefined;
    }

    const timeRelativeToPage = Math.max(ceilPrecision(pageTime, 9), 0);

    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      if (subtitle.startTime <= timeRelativeToPage && subtitle.endTime >= timeRelativeToPage) {
        return subtitle;
      }
    }

    return undefined;
  }

  public async updateAllSubtitlesToLetterCase(letterCase: LetterCase): Promise<void> {
    const wasPlaying = this.page.poster.isPlaying();

    if (wasPlaying) {
      await this.page.poster.pause();
    }

    const newSubtitlesHashmap: Record<string, DeepPartial<SubtitleObject>> = {};
    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      if (subtitle.animationStyle !== SubtitleTemplateType.SIMPLE) {
        const wordsWithAppliedLetterCase = [];
        for (const word of subtitle.words) {
          wordsWithAppliedLetterCase.push({...word, text: applyLetterCase(word.text, letterCase)});
        }

        newSubtitlesHashmap[subtitle.subtitleUID] = {
          text: applyLetterCase(subtitle.text, letterCase),
          words: wordsWithAppliedLetterCase,
        };
      } else {
        newSubtitlesHashmap[subtitle.subtitleUID] = {
          text: applyLetterCase(subtitle.text, letterCase),
        };
      }
    }

    await this.updateFromObject({
      subtitlesHashmap: newSubtitlesHashmap,
    });

    if (wasPlaying) {
      await this.page.poster.play();
    }
  }

  public async updateAllSubtitlesStylesFromSubtitleObject(
    subtitleObject: DeepPartial<SubtitleObject>,
    {undoable = true, fireModifiedEvent = true, updateRedux = true, onError = noop}: TranscriptUpdateFromObjectOpts = {},
    updatedFromTemplate = false
  ): Promise<void> {
    const wasPlaying = this.page.poster.isPlaying();

    if (wasPlaying) {
      await this.page.poster.pause();
    }

    const newSubtitlesHashmap = this.getNewSubtitlesHashmapToUpdateAllSubtitlesSyyles(subtitleObject, updatedFromTemplate);

    this.previewImageUrl = undefined;
    this.areFontsLoaded = false;

    await this.updateFromObject(
      {
        subtitlesHashmap: newSubtitlesHashmap,
      },
      {undoable, updateRedux, fireModifiedEvent, onError}
    );

    if (wasPlaying) {
      await this.page.poster.play();
    }
  }

  public updateTranscriptGroupItemsDimensionsAndAlignment(): void {
    if (!this.areFontsLoaded) {
      return;
    }

    this.updateSubtitleDimensions();
    this.updateTranscriptHeight();
    this.updateSubtitlesAlignment();
  }

  public async onTextEditingExit(): Promise<void> {
    this.smallestWidthNeeded = undefined;
    this.setTranscriptItemAndFabricGroupWidth(Math.max(this.getSmallestWidthNeededBySubtitleFabricTextBox(), this.fabricObject.width));

    this.syncAllSubtitles();

    await this.updateFabricObject();

    this.fabricObject.set({
      subTargetCheck: false,
      interactive: false,
    });

    unhideCustomControls(this.page.hashedID);
  }

  public async onTextEditingEnter(): Promise<void> {
    this.smallestWidthNeeded = undefined;

    hideCustomControls(this.page.hashedID);

    if (this.page.poster.isPlaying()) {
      await this.page.poster.pause();
    }
  }

  public onTextChanged(): void {
    this.smallestWidthNeeded = undefined;

    this.setTranscriptItemAndFabricGroupWidth(Math.max(this.getSmallestWidthNeededBySubtitleFabricTextBox(), this.fabricObject.width));
  }

  public override doesfabricObjBelongtoItem(fabricObj: FabricObject | Group): boolean {
    return this.fabricObject === fabricObj || this.fabricObject === fabricObj.group?.group;
  }

  public override getDuration(): number {
    let lastSubtitleEndTime: number | undefined;
    let firstSubtitleStartTime: number | undefined;

    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      if (firstSubtitleStartTime === undefined || (firstSubtitleStartTime && subtitle.startTime < firstSubtitleStartTime)) {
        firstSubtitleStartTime = subtitle.startTime;
      }

      if (lastSubtitleEndTime === undefined || (lastSubtitleEndTime && subtitle.endTime > lastSubtitleEndTime)) {
        lastSubtitleEndTime = subtitle.endTime;
      }
    }

    if (lastSubtitleEndTime === undefined || firstSubtitleStartTime === undefined) {
      throw new Error(`Failed to get duration for transcript!`);
    }

    return lastSubtitleEndTime - firstSubtitleStartTime;
  }

  public override isStreamingMediaItem(): boolean {
    return true;
  }

  public override async seek(time: number): Promise<void> {
    this.seekToSubtitleAtTime(time);
  }

  public addSubtitle(itemToAdd: Subtitle): void {
    this.subtitlesHashmap[itemToAdd.subtitleUID] = itemToAdd;
    addItemsToGroupWithOriginalScale(this.fabricObject, [itemToAdd.fabricObject]);
  }

  /**
   * @returns true if the subtitle was deleted, false if not found
   */
  public deleteSubtitle(subtitleId: string): boolean {
    if (!this.subtitlesHashmap[subtitleId]) {
      return false;
    }

    if (Object.keys(this.subtitlesHashmap).length === 1) {
      this.page.poster.deleteItemById(this.uid);
      return true;
    }

    this.removeSubtitle(subtitleId);

    this.page.poster.history.addPosterHistory();
    this.page.fire('modified');
    this.page.poster.redux.updateReduxData();
    // Deleting the subtitle that defined the page's max end time shrinks getDuration();
    // pull the playhead back to the new max so it isn't stranded past the timeline width.
    void this.page.clampCurrentTime();

    return true;
  }

  protected override initEvents(): void {
    super.initEvents();
    this.fabricObject.on('mousedown:before', this.onMouseDown.bind(this));
    this.fabricObject.on('mouseup', this.onMouseUp.bind(this));
    this.fabricObject.on('deselected', this.onDeselected.bind(this));
  }

  public calculateSmallestWidthNeededBySubtitleFabricTextBox(): number {
    if (this.getAnySubtitle().animationStyle === SubtitleTemplateType.ONE_WORD) {
      return this.calculateSmallestWidthNeededBySingleWordSubtitleFabricTextbox();
    } else {
      let maxSubtitleMinWidth = 0;

      for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
        const minItemWidth = subtitle.getSmallestWidthNeededByFabricTextBox();
        if (minItemWidth > maxSubtitleMinWidth) {
          maxSubtitleMinWidth = minItemWidth;
        }
      }

      return maxSubtitleMinWidth;
    }
  }

  public getEndTimeOfLastItem(): number {
    const listOfEndTimes = [];
    for (const [, item] of Object.entries(this.subtitlesHashmap)) {
      listOfEndTimes.push(item.endTime);
    }

    return Math.max(...listOfEndTimes);
  }

  public doHideAnimationOptionsAndPresets(): boolean {
    return isRTLScript(this.getAnySubtitle().sentenceTextStyles.script);
  }

  public override getScaleForShadow(): number {
    return 1;
  }

  public getSmallestWidthNeededBySubtitleFabricTextBox(): number {
    if (!this.areFontsLoaded) {
      // cannot cache the result in this case, because once the font gets loaded in the async updateFromObject call of the subtitle class, the fabric object width needed will change
      return this.calculateSmallestWidthNeededBySubtitleFabricTextBox();
    }

    if (!this.smallestWidthNeeded) {
      this.smallestWidthNeeded = this.calculateSmallestWidthNeededBySubtitleFabricTextBox();
    }

    return this.smallestWidthNeeded;
  }

  public setTranscriptItemAndFabricGroupWidth(width: number): void {
    this.fabricObject.set({
      width,
    });
    this.updateTranscriptGroupItemsDimensionsAndAlignment();
  }

  protected getShadow(): Shadow | null {
    return this.getAnySubtitle().getShadow();
  }

  protected getFabricObjectForItem(opts: InitItemOpts = {}): Promise<Group> {
    const subtitleTextAndBackground: Group[] = [];

    return new Promise((resolve) => {
      Object.values(this.subtitlesHashmap).forEach((subtitle) => {
        subtitleTextAndBackground.push(subtitle.getFabricObject());
      });

      const groupItem = new Group(subtitleTextAndBackground, {
        ...super.getCommonOptions(),
        width: opts?.width ?? 300,
        height: opts?.height ?? 200,
        scaleX: opts?.scaleX ?? 1,
        scaleY: opts?.scaleY ?? 1,
        perPixelTargetFind: true,
        layoutManager: new LayoutManager(new FixedLayout()),
      });
      resolve(groupItem);
    });
  }

  private ensureMinDimensionsAreSatisfied(): void {
    if (!this.areFontsLoaded) {
      return;
    }

    this.smallestWidthNeeded = undefined;

    const minWidth = this.getSmallestWidthNeededBySubtitleFabricTextBox();
    const height = this.getLargestSubtitleFabricObjectHeight();

    if (this.fabricObject.width < minWidth) {
      this.fabricObject.set({
        width: minWidth,
        height: height,
      });
    }
  }

  private calculateSmallestWidthNeededBySingleWordSubtitleFabricTextbox(): number {
    let maxSubtitleMinWidth = 0;

    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      for (const word of subtitle.words) {
        const text = new IText(word.text, {
          shadow: subtitle.getShadow(),
          ...subtitle.sentenceTextStyles.getTextStyles(subtitle.textFabricObject.width, subtitle.textFabricObject.height),
        });

        const minItemWidth = text.width + BACKGROUND_PADDING * 2;
        if (minItemWidth > maxSubtitleMinWidth) {
          maxSubtitleMinWidth = minItemWidth;
        }
      }
    }

    const activeSubtitle = this.getCurrentSubtitle();

    if (activeSubtitle?.textFabricObject.isEditing) {
      const text = new IText(activeSubtitle.textFabricObject.text, {
        shadow: activeSubtitle.getShadow(),
        ...this.getAnySubtitle().sentenceTextStyles.getTextStyles(this.getAnySubtitle().textFabricObject.width, this.getAnySubtitle().textFabricObject.height),
      });

      const minItemWidth = text.width + BACKGROUND_PADDING * 2;
      if (minItemWidth > maxSubtitleMinWidth) {
        maxSubtitleMinWidth = minItemWidth;
      }
    }
    return maxSubtitleMinWidth;
  }

  private onMouseDown(): void {
    if (!(this.page.fabricCanvas instanceof Canvas)) {
      throw new Error(`Page canvas neeed to be of type canvas`);
    }
    this.wasItemSelectedOnMouseDown = this.page.getSelectedItems()[0] === this;
  }

  private onDeselected(): void {
    const subtitlesHashmapWithoutEmptySubtitles: Record<string, Subtitle> = {};

    Object.entries(this.subtitlesHashmap).forEach(([id, subtitle]) => {
      if (subtitle.text !== '') {
        subtitlesHashmapWithoutEmptySubtitles[id] = subtitle;
      }
    });

    if (Object.values(subtitlesHashmapWithoutEmptySubtitles).length) {
      void this.updateFromObject({subtitlesHashmap: subtitlesHashmapWithoutEmptySubtitles}, {undoable: false});
    } else {
      this.page.poster.deleteItemById(this.uid);
    }
  }

  private onMouseUp(e: ObjectEvents['mouseup']): void {
    if (!(this.page.fabricCanvas instanceof Canvas)) {
      throw new Error(`Page canvas neeed to be of type canvas`);
    }

    if (e.isClick && this.wasItemSelectedOnMouseDown && !this.fabricObject.getActiveControl()) {
      this.fabricObject.set({
        subTargetCheck: true,
        interactive: true,
      });
      const currentSubtitleTextbox = this.getCurrentSubtitle()?.textFabricObject;
      if (currentSubtitleTextbox) {
        this.page.fabricCanvas.setActiveObject(currentSubtitleTextbox);
        currentSubtitleTextbox.enterEditing(e.e);
      }
    }
  }

  private updateTranscriptHeight(): void {
    const heightToSet = this.getLargestSubtitleFabricObjectHeight();

    if (heightToSet === this.fabricObject.height) {
      return;
    }

    this.fabricObject.set({
      height: heightToSet,
    });
  }

  private getLargestSubtitleFabricObjectHeight(): number {
    if (this.getAnySubtitle().animationStyle === SubtitleTemplateType.ONE_WORD) {
      let maxHeight = -1;

      for (const subtitle of Object.values(this.subtitlesHashmap)) {
        for (const word of subtitle.words) {
          const tempTextFabricObject = new IText(word.text, {
            shadow: subtitle.getShadow(),
            ...subtitle.sentenceTextStyles.getTextStyles(subtitle.textFabricObject.width, subtitle.textFabricObject.height),
          });

          if (tempTextFabricObject.height > maxHeight) {
            maxHeight = tempTextFabricObject.height;
          }
        }
      }

      return maxHeight + BACKGROUND_PADDING * 2;
    }

    let maxObjectHeight = 0;
    for (const object of this.fabricObject.getObjects()) {
      if (object.height > maxObjectHeight) {
        maxObjectHeight = object.height;
      }
    }

    return maxObjectHeight;
  }

  private updateSubtitleDimensions(): void {
    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      subtitle.setSubtitleFabricGroupItemsDimensionsAndAlignment(this.fabricObject.width);
    }
  }

  private removeSubtitle(uid: string): void {
    if (this.hasItem(uid)) {
      this.subtitlesHashmap[uid].onRemove();
      this.fabricObject.remove(this.subtitlesHashmap[uid].fabricObject);
      delete this.subtitlesHashmap[uid];
    }
  }

  private hasItem(uid: string): boolean {
    return uid in this.subtitlesHashmap;
  }

  private updateSubtitlesAlignment(): void {
    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      if (this.verticalAlign === TextVerticalAlignType.TOP) {
        subtitle.fabricObject.setPositionByOrigin(new Point(0, -this.fabricObject.height / 2), 'center', 'top');
      } else if (this.verticalAlign === TextVerticalAlignType.CENTER) {
        subtitle.fabricObject.setPositionByOrigin(new Point(0, 0), 'center', 'center');
      } else if (this.verticalAlign === TextVerticalAlignType.BOTTOM) {
        subtitle.fabricObject.setPositionByOrigin(new Point(0, this.fabricObject.height / 2 - subtitle.fabricObject.height), 'center', 'top');
      }
    }
  }

  private bringToFront(): void {
    if (!this.page.fabricCanvas.contains(this.fabricObject)) {
      return;
    }
    this.page.fabricCanvas.bringObjectToFront(this.fabricObject);
  }

  private syncAllSubtitles(): void {
    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      subtitle.syncSubtitle();
    }
  }

  public doesSubtitleExistInItem(subtitleId: string): boolean {
    const itemIds = Object.keys(this.subtitlesHashmap);
    return itemIds.includes(subtitleId);
  }

  public getNextSubtitleId(currentSubtitleId: string): string | null {
    if (!this.doesSubtitleExistInItem(currentSubtitleId)) {
      return null;
    }
    const subtitlesOrder = this.getSubtitleIdsInOrder();
    const currentSubtitleIndex = subtitlesOrder.indexOf(currentSubtitleId);
    if (currentSubtitleIndex === subtitlesOrder.length - 1) {
      return null;
    }

    return subtitlesOrder[currentSubtitleIndex + 1];
  }

  public getPreviousSubtitleId(currentSubtitleId: string): string | null {
    if (this.doesSubtitleExistInItem(currentSubtitleId)) {
      return null;
    }

    const subtitlesOrder = this.getSubtitleIdsInOrder();
    const currentSubtitleIndex = subtitlesOrder.indexOf(currentSubtitleId);

    if (currentSubtitleIndex === 0) {
      return null;
    }

    return subtitlesOrder[currentSubtitleIndex - 1];
  }

  public isAddAfterSubtitleDisabled(subtitleId: string): boolean {
    if (!this.doesSubtitleExistInItem(subtitleId)) {
      console.error("subtitle does not exist in 'isAddAfterSubtitleDisabled' function");
      return true;
    }
    if (this.subtitlesHashmap[subtitleId].endTime + 3 >= 600) {
      return true;
    }

    if (Object.values(this.subtitlesHashmap).length >= 1000) {
      return true;
    }

    const nextSubtitleId = this.getNextSubtitleId(subtitleId);
    if (!nextSubtitleId) {
      return false;
    }

    const currentSubtitleEndTime = this.subtitlesHashmap[subtitleId].endTime;
    const nextSubtitleStartTime = this.subtitlesHashmap[nextSubtitleId].startTime;
    return Math.abs(nextSubtitleStartTime - currentSubtitleEndTime) <= 1;
  }

  private getCurrentSubtitle(): Subtitle | undefined {
    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      if (subtitle.isCurrentlyActiveSubtitle) {
        return subtitle;
      }
    }

    return undefined;
  }

  private getNewSubtitlesHashmapToUpdateAllSubtitlesSyyles(subtitleObject: DeepPartial<SubtitleObject>, fromTemplate = false): Record<string, DeepPartial<SubtitleObject>> {
    const newSubtitlesHashmap: Record<string, DeepPartial<SubtitleObject>> = {};
    const stylesToUpdate: DeepPartial<SubtitleObject> = {};

    for (const [, subtitle] of Object.entries(this.subtitlesHashmap)) {
      const sentenceTextStyles = subtitleObject.sentenceTextStyles;
      const activeWordTextStyles = subtitleObject.activeWordTextStyles;

      if (sentenceTextStyles) {
        stylesToUpdate.sentenceTextStyles = {
          ...subtitleObject.sentenceTextStyles,
          script: subtitle.sentenceTextStyles.script,
        };

        // for transcript items in a language having a special script (e.g. Arabic),
        // we want to apply the recommended font first, so that the templates still look good
        // for these languages
        if (fromTemplate && subtitle.sentenceTextStyles.script) {
          stylesToUpdate.sentenceTextStyles.fontFamily = getRecommendedFont(subtitle.sentenceTextStyles.script);
        }
      }

      if (activeWordTextStyles) {
        stylesToUpdate.activeWordTextStyles = {
          ...subtitleObject.activeWordTextStyles,
          script: subtitle.activeWordTextStyles.script,
        };

        // for transcript items in a language having a special script (e.g. Arabic),
        // we want to apply the recommended font first, so that the templates still look good
        // for these languages
        if (fromTemplate && subtitle.activeWordTextStyles.script) {
          stylesToUpdate.activeWordTextStyles.fontFamily = getRecommendedFont(subtitle.activeWordTextStyles.script);
        }
      }

      newSubtitlesHashmap[subtitle.subtitleUID] = {
        ...subtitle.toObject(),
        ...subtitleObject,
        ...stylesToUpdate,
      };
    }

    return newSubtitlesHashmap;
  }
}

export const getSelectedTemplateIdForAllSubtitles = (): string => {
  const transcriptItems = window.posterEditor?.whiteboard?.getCurrentPage().items.getAllTranscriptItems();

  if (transcriptItems?.length) {
    const latestAddedTranscriptItem = transcriptItems[transcriptItems.length - 1];

    if (latestAddedTranscriptItem) {
      return latestAddedTranscriptItem.getAnySubtitle().selectedTemplateId;
    }
  }

  return getDefaultSelectedSubtitleTemplateProperties().id;
};

export const getStylesToApplyForAllSubtitles = (selectedTemplateId: string): DeepPartial<SubtitleStylesAndAnimation> => {
  const transcriptItems = window.posterEditor?.whiteboard?.getCurrentPage().items.getAllTranscriptItems();

  if (transcriptItems?.length) {
    const latestAddedTranscriptItem = transcriptItems[transcriptItems.length - 1];

    if (latestAddedTranscriptItem) {
      return {
        sentenceTextStyles: latestAddedTranscriptItem.getAnySubtitle().sentenceTextStyles,
        activeWordTextStyles: latestAddedTranscriptItem.getAnySubtitle().activeWordTextStyles,
        shadow: convertFabricShadowObjectToShadowOrGlowOptionsObject(
          (latestAddedTranscriptItem.getAnySubtitle().fabricObject.shadow?.toObject() as SerializedShadowOptions) ?? null,
          latestAddedTranscriptItem.getScaleForShadow()
        ),
        backgroundFill: latestAddedTranscriptItem.getAnySubtitle().backgroundFill,
        backgroundBorderRadius: latestAddedTranscriptItem.getAnySubtitle().backgroundBorderRadius,
        animationStyle: latestAddedTranscriptItem.getAnySubtitle().animationStyle,
      };
    }
  }

  return getAllAvailableTemplateStylesAndProperties()[selectedTemplateId].templateStyles.stylesToApply;
};

export const createTranscriptItem = async (page: Page, itemData: NewTranscriptData, optionsToAdd?: DeepPartial<TranscriptItemObject>): Promise<TranscriptItem> => {
  const widthOfThePoster = page.poster.width;
  const heightOfThePoster = page.poster.height;

  if (!widthOfThePoster || !heightOfThePoster) {
    throw new Error('Could not get width and height of the poster');
  }

  const fontSizeLowerLimit = widthOfThePoster > heightOfThePoster ? widthOfThePoster / DEFAULT_FONT_SIZE / 2 : heightOfThePoster / DEFAULT_FONT_SIZE / 2;

  const fontSize = Math.max(widthOfThePoster < heightOfThePoster ? widthOfThePoster / DEFAULT_FONT_SIZE : heightOfThePoster / DEFAULT_FONT_SIZE, fontSizeLowerLimit);

  const subtitlesHashmap: Record<string, DeepPartial<SubtitleObject>> = {};

  const selectedTemplateId = itemData.selectedSubtitleTemplateId ?? getSelectedTemplateIdForAllSubtitles();
  const appliedStyles = itemData.styles ?? getStylesToApplyForAllSubtitles(selectedTemplateId);

  const {sentenceTextStyles, activeWordTextStyles, shadow, ...styles} = appliedStyles;

  for (const subtitle of itemData.subtitles) {
    const script = getScriptForText(subtitle.text);

    subtitlesHashmap[subtitle.id] = {
      words: subtitle.words,
      subtitleUID: subtitle.id,
      text: subtitle.text,
      startTime: subtitle.startTime,
      endTime: subtitle.endTime,
      hasUserEdited: !itemData.areAIGenerated,
      selectedTemplateId,
      sentenceTextStyles: {
        ...sentenceTextStyles,
        fontSize,
        script: script,
        fontFamily: script ? getRecommendedFont(script) : sentenceTextStyles?.fontFamily,
      },
      activeWordTextStyles: {
        ...activeWordTextStyles,
        fontSize,
        script: script,
        fontFamily: script ? getRecommendedFont(script) : activeWordTextStyles?.fontFamily,
      },
      ...styles,
    };
  }

  const item = (await createItemFromObject(page, {
    gitype: ITEM_TYPE.TRANSCRIPT,
    height: 0,
    width: page.poster.width - TRANSCRIPT_ITEM_PADDING - TRANSCRIPT_ITEM_PADDING,
    ...itemData,
    ...optionsToAdd,
    subtitlesHashmap,
  })) as TranscriptItem;

  const allTranscriptItems = page.items.getAllTranscriptItems();
  const {left, top} = item.getInitialCoordinatesForTranscript(allTranscriptItems.length + 1);
  item.fabricObject.left = left;
  item.fabricObject.top = top;

  if (shadow) {
    await item.updateAllSubtitlesStylesFromSubtitleObject(
      {
        shadow: convertShadowOptionsObjectToFabricShadowObject(shadow as ShadowOrGlowOptionsObject, item.getScaleForShadow()),
      },
      {undoable: false}
    );
  }

  return item;
};

